import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchX } from "lucide-react";
import api from "../../lib/api";
import { MKT_CSS, MktHeader, ItemCard, FreelancerCard } from "./MktShared";
import { LoadingRows } from "../../components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function MarketplaceSearch() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const category = sp.get("category") || "";
  const tab = sp.get("tab") || "all";
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/mkt/search", { params: { q, category } }).then((r) => setData(r.data))
      .catch(() => setData({ services: [], products: [], courses: [], freelancers: [] }));
  }, [q, category]);

  const setTab = (t) => setSp((p) => { p.set("tab", t); return p; }, { replace: true });
  const total = data ? data.services.length + data.products.length + data.courses.length + data.freelancers.length : 0;

  const Grid = ({ items, kind }) => (
    items.length === 0
      ? <p className="text-sm text-muted-foreground py-10 text-center">No results here yet.</p>
      : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{items.map((it, i) => <ItemCard key={it.id} item={it} kind={kind} delay={i} />)}</div>
  );

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mkt-sec-title" data-testid="mkt-search-title">
          {category ? category : q ? `Results for “${q}”` : "Explore the Marketplace"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5" data-testid="mkt-search-count">{data ? `${total} result${total === 1 ? "" : "s"}` : "Searching…"}</p>

        {!data ? <div className="mt-8"><LoadingRows rows={3} /></div> : total === 0 ? (
          <div data-testid="mkt-search-empty" className="flex flex-col items-center text-center py-24 gap-3">
            <SearchX className="h-10 w-10 text-muted-foreground" />
            <p className="font-display font-bold text-lg">No matches found</p>
            <p className="text-sm text-muted-foreground max-w-sm">Try a different keyword, or browse categories from the marketplace home.</p>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList data-testid="mkt-search-tabs" className="flex-wrap h-auto">
              <TabsTrigger value="all" data-testid="mkt-tab-all">All ({total})</TabsTrigger>
              <TabsTrigger value="services" data-testid="mkt-tab-services">Services ({data.services.length})</TabsTrigger>
              <TabsTrigger value="products" data-testid="mkt-tab-products">Products ({data.products.length})</TabsTrigger>
              <TabsTrigger value="courses" data-testid="mkt-tab-courses">Courses ({data.courses.length})</TabsTrigger>
              <TabsTrigger value="freelancers" data-testid="mkt-tab-freelancers">Freelancers ({data.freelancers.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-6 space-y-10">
              {data.services.length > 0 && <div><h3 className="font-display font-bold text-lg mb-4">Services</h3><Grid items={data.services} kind="service" /></div>}
              {data.products.length > 0 && <div><h3 className="font-display font-bold text-lg mb-4">Digital Products</h3><Grid items={data.products} kind="product" /></div>}
              {data.courses.length > 0 && <div><h3 className="font-display font-bold text-lg mb-4">Courses</h3><Grid items={data.courses} kind="course" /></div>}
              {data.freelancers.length > 0 && (
                <div><h3 className="font-display font-bold text-lg mb-4">Freelancers</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{data.freelancers.map((f, i) => <FreelancerCard key={f.slug} f={f} delay={i} />)}</div>
                </div>)}
            </TabsContent>
            <TabsContent value="services" className="mt-6"><Grid items={data.services} kind="service" /></TabsContent>
            <TabsContent value="products" className="mt-6"><Grid items={data.products} kind="product" /></TabsContent>
            <TabsContent value="courses" className="mt-6"><Grid items={data.courses} kind="course" /></TabsContent>
            <TabsContent value="freelancers" className="mt-6">
              {data.freelancers.length === 0
                ? <p className="text-sm text-muted-foreground py-10 text-center">No freelancers found.</p>
                : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{data.freelancers.map((f, i) => <FreelancerCard key={f.slug} f={f} delay={i} />)}</div>}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
